import { all, put, select, takeEvery } from 'redux-saga/effects';
import { setCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { removeEditorDraft, saveEditorDraft } from './actions';
import { EDITOR_DRAFT_EXPIRATION_DELTA } from './constants';
import type {
  EditorDraftLocalStorageState,
  EditorDraftsLocalStorageState,
} from './types';
import { getEditorDraftsState } from './selectors';

function cleanExpiredDrafts(draftsState: EditorDraftsLocalStorageState) {
  const newState: EditorDraftsLocalStorageState = {};

  for (const editorId in draftsState) {
    const { updatedAt } = draftsState[editorId];
    const minUpdatedDate = Date.now() - EDITOR_DRAFT_EXPIRATION_DELTA;
    if (updatedAt >= minUpdatedDate) {
      newState[editorId] = draftsState[editorId];
    }
  }

  return newState;
}

function* saveEditorDraftHandler({
  payload: { editorId, editorState },
}: ReturnType<typeof saveEditorDraft>) {
  if (!editorState) {
    return;
  }
  const data: EditorDraftLocalStorageState = {
    editorState,
    updatedAt: Date.now(),
  };

  const draftsState = yield select(getEditorDraftsState) || {};
  const mergedDraftsState = {
    // While we're updating the drafts state, let's clean up the ones that are expired
    ...cleanExpiredDrafts(draftsState),
    [editorId]: data,
  };

  yield put(
    setCompliantLocalStorageItem({
      key: CompliantLocalStorageKeys.EDITOR_DRAFTS,
      value: JSON.stringify(mergedDraftsState),
    })
  );
}

function* removeEditorDraftHandler({
  payload: { editorId },
}: ReturnType<typeof removeEditorDraft>) {
  const draftsState = yield select(getEditorDraftsState) || {};
  delete draftsState[editorId];

  yield put(
    setCompliantLocalStorageItem({
      key: CompliantLocalStorageKeys.EDITOR_DRAFTS,
      value: JSON.stringify(draftsState),
    })
  );
}

export function* draftsSaga() {
  yield all([
    takeEvery(saveEditorDraft, saveEditorDraftHandler),
    takeEvery(removeEditorDraft, removeEditorDraftHandler),
  ]);
}
